/**
 * BlockSlotDrawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Drawer de bloqueio de horário.
 * Suporta dois modos:
 *   - Criação: quando `appointment` é null/undefined → cria novo bloqueio
 *   - Edição:  quando `appointment` é passado → edita bloqueio existente
 *
 * @module BlockSlotDrawer
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { Blocked } from '../../../../../consts/AppointmentsState';
import { WEEKDAYS_LONG, MONTHS } from '../../constants/calendarConstants';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import { getSlotIntervalMinutes } from '../../../../../consts/CalendarConfig';
import styles from './BlockSlotDrawer.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5A80FB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// ── Motivos pré-definidos ─────────────────────────────────────────────────────

const BLOCK_REASONS = [
  { value: '',               label: 'Selecionar motivo...' },
  { value: 'Reunião interna', label: 'Reunião interna' },
  { value: 'Almoço',         label: 'Almoço' },
  { value: 'Intervalo',      label: 'Intervalo' },
  { value: 'Manutenção',     label: 'Manutenção' },
  { value: 'Feriado',        label: 'Feriado' },
  { value: 'Outro',          label: 'Outro' },
];

const KNOWN_REASONS = BLOCK_REASONS.map(r => r.value).filter(Boolean);

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatHeaderDate(date) {
  if (!date) return '';
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const weekday = WEEKDAYS_LONG[date.getDay()];
  return `${day} ${month} ${year} • ${weekday}`;
}

function formatTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function formatDateInput(date) {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

function calcDuration(startHour, startMinute, endHour, endMinute) {
  return (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
}

function formatDurationForAPI(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

function formatDateForAPI(date, hour, minute) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(hour).padStart(2, '0');
  const min = String(minute).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:00`;
}

/**
 * Dado o patient label salvo (ex: "Reunião interna", "Bloqueado", ou texto livre),
 * retorna { reason, customReason } para inicializar o formulário.
 */
function parseReasonFromPatient(patient) {
  if (!patient || patient === 'Bloqueado') return { reason: '', customReason: '' };
  if (KNOWN_REASONS.includes(patient)) return { reason: patient, customReason: '' };
  // Texto livre → "Outro"
  return { reason: 'Outro', customReason: patient };
}

/**
 * Dado duration em minutos (normalizeAppointment já converte para número),
 * calcula o horário de fim a partir do slot de início.
 */
function calcEndFromDuration(startHour, startMinute, durationMinutes) {
  const totalMinutes = startHour * 60 + startMinute + durationMinutes;
  return {
    endHour: Math.floor(totalMinutes / 60) % 24,
    endMinute: totalMinutes % 60,
  };
}

/**
 * Drawer de bloqueio de horário (criação e edição).
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen         - Se o drawer está aberto
 * @param {Object}   props.slot           - { date: Date, hour: number, minute: number }
 * @param {Object}   [props.appointment]  - Agendamento existente (modo edição)
 * @param {Function} props.onClose        - Fechar drawer
 * @param {Function} props.onSave         - Callback após salvar
 * @param {Function} [props.onGhostChange] - Callback com objeto fantasma para o calendário ({ date, duration } | null)
 * @param {string|number} [props.initialResourceId] - Agenda já escolhida ao criar (coluna do modo "Profissionais")
 */
export default function BlockSlotDrawer({
  isOpen,
  slot,
  appointment,
  onClose,
  onSave,
  onGhostChange,
  initialResourceId,
}) {
  const { SetAppointment, DeleteAppointment, CompanyConfig } = DataProviderInstance();

  const isEditMode = Boolean(appointment?.id || appointment?.Id);

  // ── Lista de profissionais disponíveis ─────────────────────────────────────
  const resources = useMemo(() => CompanyConfig?.Resources || [], [CompanyConfig]);

  // ── Estado do formulário ────────────────────────────────────────────────────

  const [endHour, setEndHour] = useState(0);
  const [endMinute, setEndMinute] = useState(0);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── Escopo do bloqueio: 'company' = geral, 'resource' = profissional específico
  const [blockScope, setBlockScope] = useState('company');
  const [selectedResourceId, setSelectedResourceId] = useState('');

  // ── Inicializa campos ao abrir ──────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !slot) return;

    setErrors({});
    setIsLoading(false);

    if (isEditMode && appointment) {
      // Modo edição: preenche com dados existentes
      const durationMinutes = Number(appointment.duration) || 60;
      const { endHour: eh, endMinute: em } = calcEndFromDuration(slot.hour, slot.minute, durationMinutes);
      setEndHour(eh);
      setEndMinute(em);

      const { reason: r, customReason: cr } = parseReasonFromPatient(appointment.patient);
      setReason(r);
      setCustomReason(cr);

      // Inicializa escopo baseado no resource do agendamento existente
      const existingResource = appointment.resource;
      if (existingResource && existingResource !== null && existingResource !== '' && existingResource !== '0') {
        setBlockScope('resource');
        setSelectedResourceId(String(existingResource));
      } else {
        setBlockScope('company');
        setSelectedResourceId('');
      }
    } else {
      // Modo criação: padrão de 1 hora; escopo geral, ou a agenda da coluna clicada
      const endDate = new Date();
      endDate.setHours(slot.hour + 1, slot.minute, 0, 0);
      setEndHour(endDate.getHours());
      setEndMinute(endDate.getMinutes());
      setReason('');
      setCustomReason('');
      const hasInitialResource = initialResourceId != null && initialResourceId !== '';
      setBlockScope(hasInitialResource ? 'resource' : 'company');
      setSelectedResourceId(hasInitialResource ? String(initialResourceId) : '');
    }
  }, [isOpen, slot, appointment, isEditMode, initialResourceId]);

  // ── Emite fantasma para o calendário sempre que início/fim mudar ────────────

  useEffect(() => {
    if (!isOpen || !slot || !onGhostChange) return;

    const durationMinutes = calcDuration(slot.hour, slot.minute, endHour, endMinute);
    if (durationMinutes <= 0) {
      onGhostChange(null);
      return;
    }

    const ghost = {
      id: '__ghost__',
      status: 'blocked',
      date: formatDateForAPI(slot.date, slot.hour, slot.minute),
      duration: formatDurationForAPI(durationMinutes),
      patient: 'Bloqueado',
      resource: blockScope === 'resource' && selectedResourceId !== '' ? selectedResourceId : null,
    };
    onGhostChange(ghost);

    return () => {
      onGhostChange(null);
    };
  }, [isOpen, slot, endHour, endMinute, blockScope, selectedResourceId, onGhostChange]);

  // ── Validação ───────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const errs = {};
    if (!slot) return errs;
    const duration = calcDuration(slot.hour, slot.minute, endHour, endMinute);
    if (duration <= 0) {
      errs.endTime = 'O horário de fim deve ser após o início';
    }
    return errs;
  }, [slot, endHour, endMinute]);

  // ── Salvar ──────────────────────────────────────────────────────────────────

  const handleSave = useCallback(() => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsLoading(true);
    setErrors({});

    const durationMinutes = calcDuration(slot.hour, slot.minute, endHour, endMinute);
    const resolvedReason = reason === 'Outro' ? customReason.trim() : reason;
    const patientLabel = resolvedReason || 'Bloqueado';
    const durationStr = formatDurationForAPI(durationMinutes);

    // Resolve o resource a salvar:
    // - 'company' → null (bloqueio geral da empresa)
    // - 'resource' → ID do profissional selecionado (ou null se nenhum selecionado)
    const resolvedResource = blockScope === 'resource' && selectedResourceId
      ? selectedResourceId
      : null;

    if (isEditMode) {
      // ── Editar bloqueio: delete + create (API não aceita patient como texto no update) ──
      const id = appointment.id || appointment.Id;
      const newAppointmentData = {
        status: Blocked,
        date: formatDateForAPI(slot.date, slot.hour, slot.minute),
        patient: patientLabel,
        resource: resolvedResource,
        procedure: null,
        duration: durationStr,
        price: 0,
        companyId: CompanyConfig?.Id || CompanyConfig?.id || '',
        roomId: null,
        notes: null,
      };

      DeleteAppointment(id, () => {
        SetAppointment(newAppointmentData, (result) => {
          setIsLoading(false);
          if (result?.error) {
            setErrors({ general: 'Erro ao atualizar bloqueio. Tente novamente.' });
            return;
          }
          const saved = { ...newAppointmentData, id: result?.id || result?.Id || null };
          onSave?.(saved);
          onClose?.();
        });
      });
    } else {
      // ── Criar novo bloqueio ───────────────────────────────────────────────
      const appointmentData = {
        status: Blocked,
        date: formatDateForAPI(slot.date, slot.hour, slot.minute),
        patient: patientLabel,
        resource: resolvedResource,
        procedure: null,
        duration: durationStr,
        price: 0,
        companyId: CompanyConfig?.Id || CompanyConfig?.id || '',
        roomId: null,
        notes: null,
      };

      SetAppointment(appointmentData, (result) => {
        setIsLoading(false);
        if (result?.error) {
          setErrors({ general: 'Erro ao bloquear horário. Tente novamente.' });
          return;
        }
        const saved = { ...appointmentData, id: result?.id || result?.Id || null };
        onSave?.(saved);
        onClose?.();
      });
    }
  }, [
    validate, slot, endHour, endMinute, reason, customReason,
    blockScope, selectedResourceId,
    isEditMode, appointment, CompanyConfig, SetAppointment, DeleteAppointment,
    onSave, onClose,
  ]);

  // ── Deletar bloqueio ────────────────────────────────────────────────────────

  const handleDeleteConfirmed = useCallback(() => {
    const id = appointment?.id || appointment?.Id;
    if (!id) return;
    setIsLoading(true);
    DeleteAppointment(id, () => {
      setIsLoading(false);
      setConfirmDelete(false);
      onSave?.();
      onClose?.();
    });
  }, [appointment, DeleteAppointment, onSave, onClose]);

  if (!isOpen || !slot) return null;

  const dateLabel = formatHeaderDate(slot.date);
  const startTimeLabel = formatTime(slot.hour, slot.minute);
  const dateInputLabel = formatDateInput(slot.date);

  // Gera opções de horário para o campo "Fim" (a cada intervalo de slot da empresa, apenas após o início)
  const slotIntervalMinutes = getSlotIntervalMinutes(CompanyConfig?.TimeInterval);
  const startTotalMinutes = slot.hour * 60 + slot.minute;
  const timeOptions = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += slotIntervalMinutes) {
      if (h * 60 + m > startTotalMinutes) {
        timeOptions.push({ hour: h, minute: m, label: formatTime(h, m) });
      }
    }
  }

  const drawerTitle = isEditMode ? 'Editar bloqueio' : 'Bloquear horário';
  const saveLabel = isEditMode
    ? (isLoading ? 'Salvando...' : 'Salvar alterações')
    : (isLoading ? 'Bloqueando...' : 'Bloquear horário');

  return (
    <div className={styles.drawerContainer} role="dialog" aria-modal="true" aria-label={drawerTitle}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className={styles.drawerHeader}>
        <div className={styles.drawerHeaderTop}>
          <h2 className={styles.drawerTitle}>{drawerTitle}</h2>
          <button
            className={styles.drawerCloseBtn}
            onClick={onClose}
            aria-label="Fechar"
            title="Fechar"
            type="button"
          >
            <XIcon size={20} />
          </button>
        </div>

        <div className={styles.drawerHeaderDivider} />

        <div className={styles.slotContext}>
          <div className={styles.slotContextLeft}>
            <span className={styles.slotContextIcon} aria-hidden="true">
              <LockIcon />
            </span>
            <div className={styles.slotContextInfo}>
              <span className={styles.slotDate}>{dateLabel}</span>
              <div className={styles.slotTimeRow}>
                <span className={styles.slotTime}>{startTimeLabel}</span>
              </div>
            </div>
          </div>
          {isEditMode && (
            <button
              type="button"
              className={styles.btnDelete}
              onClick={() => setConfirmDelete(true)}
              disabled={isLoading}
              title="Deletar bloqueio"
              aria-label="Deletar bloqueio"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── Corpo ───────────────────────────────────────────────────────────── */}
      <div className={styles.drawerBody}>

        {/* Erro geral */}
        {errors.general && (
          <div className={styles.validationError} role="alert">
            {errors.general}
          </div>
        )}

        {/* ── Data + Horário início + Horário fim ────────────────────────────── */}
        <div className={styles.formGrid3}>
          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Data</label>
            <input
              type="text"
              className={styles.fieldInputReadonly}
              value={dateInputLabel}
              readOnly
              aria-label="Data do bloqueio"
            />
          </div>

          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Início</label>
            <input
              type="text"
              className={styles.fieldInputReadonly}
              value={startTimeLabel}
              readOnly
              aria-label="Horário de início"
            />
          </div>

          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Fim</label>
            <select
              className={styles.fieldSelect}
              value={`${endHour}:${endMinute}`}
              onChange={e => {
                const [h, m] = e.target.value.split(':').map(Number);
                setEndHour(h);
                setEndMinute(m);
                setErrors(prev => ({ ...prev, endTime: undefined }));
              }}
              aria-label="Horário de fim"
            >
              {timeOptions.map(opt => (
                <option key={opt.label} value={`${opt.hour}:${opt.minute}`}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.endTime && (
              <span className={styles.validationError}>{errors.endTime}</span>
            )}
          </div>
        </div>

        {/* ── Motivo ────────────────────────────────────────────────────────── */}
        <div className={styles.formSection}>
          <label className={styles.fieldLabel}>Motivo (opcional)</label>
          <select
            className={styles.fieldSelect}
            value={reason}
            onChange={e => setReason(e.target.value)}
          >
            {BLOCK_REASONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* ── Motivo personalizado (apenas quando "Outro") ───────────────────── */}
        {reason === 'Outro' && (
          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Motivo personalizado</label>
            <textarea
              className={styles.fieldTextarea}
              value={customReason}
              onChange={e => setCustomReason(e.target.value)}
              placeholder="Descreva o motivo..."
              rows={3}
              aria-label="Motivo personalizado"
            />
          </div>
        )}

        {/* ── Aplicar bloqueio para ─────────────────────────────────────────── */}
        <div className={styles.formSection}>
          <label className={styles.fieldLabel}>Aplicar bloqueio para</label>
          <div className={styles.blockScopeOptions}>
            <label className={`${styles.blockScopeOption} ${blockScope === 'company' ? styles.blockScopeOptionActive : ''}`}>
              <input
                type="radio"
                name="blockScope"
                value="company"
                checked={blockScope === 'company'}
                onChange={() => { setBlockScope('company'); setSelectedResourceId(''); }}
                className={styles.blockScopeRadio}
              />
              <span className={styles.blockScopeOptionContent}>
                <span className={styles.blockScopeOptionTitle}>Toda a empresa</span>
                <span className={styles.blockScopeOptionDesc}>Bloqueia o horário em todas as agendas</span>
              </span>
            </label>

            {resources.length > 0 && (
              <label className={`${styles.blockScopeOption} ${blockScope === 'resource' ? styles.blockScopeOptionActive : ''}`}>
                <input
                  type="radio"
                  name="blockScope"
                  value="resource"
                  checked={blockScope === 'resource'}
                  onChange={() => setBlockScope('resource')}
                  className={styles.blockScopeRadio}
                />
                <span className={styles.blockScopeOptionContent}>
                  <span className={styles.blockScopeOptionTitle}>Agenda específica</span>
                  <span className={styles.blockScopeOptionDesc}>Bloqueia apenas uma agenda</span>
                </span>
              </label>
            )}
          </div>

          {/* Dropdown de profissional — só aparece quando escopo = 'resource' */}
          {blockScope === 'resource' && resources.length > 0 && (
            <div className={styles.formSectionInner}>
              <select
                className={styles.fieldSelect}
                value={selectedResourceId}
                onChange={e => setSelectedResourceId(e.target.value)}
                aria-label="Selecionar agenda"
              >
                <option value="">Selecionar agenda...</option>
                {resources.map(r => (
                  <option key={r.Id} value={String(r.Id)}>
                    {r.Label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div className={styles.drawerFooterDivider} />
      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.btnCancel}
          onClick={onClose}
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className={isEditMode ? styles.btnSave : styles.btnBlock}
          onClick={handleSave}
          disabled={isLoading || (blockScope === 'resource' && !selectedResourceId)}
          title={blockScope === 'resource' && !selectedResourceId ? 'Selecione uma agenda para continuar' : undefined}
        >
          {isLoading && <span className={styles.spinner} />}
          {saveLabel}
        </button>
      </div>

      {/* ── Confirmação de exclusão ──────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={confirmDelete}
        title="Excluir bloqueio"
        message="Tem certeza que deseja excluir este bloqueio de horário?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
        loading={isLoading}
      />
    </div>
  );
}
